import React, { useState, useEffect } from 'react';
import AdminNavbar from './AdminNavbar';
import AdminSidebar from './AdminSidebar';
import { AlertTriangle, Settings, Clock, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

const MaintenanceMode = () => {
  const [isMaintenanceActive, setIsMaintenanceActive] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('We are currently performing scheduled maintenance. We should be back online shortly.');
  const [estimatedTime, setEstimatedTime] = useState('1 hour');
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');

  // Fetch current maintenance status on component mount
  useEffect(() => {
    const fetchMaintenanceStatus = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        console.log('GET request - Admin token:', token ? 'Token exists' : 'No token found');
        
        const response = await axios.get(`${API_BASE_URL}/maintainance-mode/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('GET request successful:', response.status);
        const data = response.data;
        setIsMaintenanceActive(data.is_maintenance);
        setMaintenanceMessage(data.maintenance_message);
        setEstimatedTime(data.estimated_time);
        setLastUpdated(data.last_updated);
      } catch (err) {
        console.error('GET request failed:', err);
        console.error('GET Error response:', err.response?.data);
        setErrorMessage('Failed to fetch maintenance status. Please refresh the page.');
      }
    };
    
    fetchMaintenanceStatus();
  }, []);

  const handleToggleMaintenance = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    const token = localStorage.getItem('admin_token');
    console.log('Admin token:', token ? 'Token exists' : 'No token found');
    console.log('Token length:', token ? token.length : 0);

    try {
      // Try different authentication headers - JWT tokens often use different formats
      const authHeaders = {
        'Content-Type': 'application/json'
      };

      // Try Bearer format first (most common for JWT)
      if (token) {
        authHeaders['Authorization'] = `Bearer ${token}`;
      }

      console.log('Sending headers:', authHeaders);

      const response = await axios.post(
        `${API_BASE_URL}/maintainance-mode/`,
        {
          is_maintenance: !isMaintenanceActive,
          maintenance_message: maintenanceMessage,
          estimated_time: estimatedTime
        },
        {
          headers: authHeaders
        }
      );
      
      const data = response.data;
      setIsMaintenanceActive(data.is_maintenance);
      setMaintenanceMessage(data.maintenance_message);
      setEstimatedTime(data.estimated_time);
      setLastUpdated(data.last_updated);
      
      setSuccessMessage(
        data.is_maintenance 
          ? 'Maintenance mode has been enabled. The site is now offline.' 
          : 'Maintenance mode has been disabled. The site is now live!'
      );
    } catch (err) {
      console.error('Error updating maintenance mode:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      console.error('Error headers:', err.response?.headers);
      
      // If Bearer token fails, try JWT format
      if (err.response?.status === 403 && token) {
        console.log('Bearer token failed, trying JWT format...');
        try {
          const response = await axios.post(
            `${API_BASE_URL}/maintainance-mode/`,
            {
              is_maintenance: !isMaintenanceActive,
              maintenance_message: maintenanceMessage,
              estimated_time: estimatedTime
            },
            {
              headers: {
                'Authorization': `JWT ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          const data = response.data;
          setIsMaintenanceActive(data.is_maintenance);
          setMaintenanceMessage(data.maintenance_message);
          setEstimatedTime(data.estimated_time);
          setLastUpdated(data.last_updated);
          
          setSuccessMessage(
            data.is_maintenance 
              ? 'Maintenance mode has been enabled. The site is now offline.' 
              : 'Maintenance mode has been disabled. The site is now live!'
          );
          return; // Success with JWT format
        } catch (jwtErr) {
          console.error('JWT format also failed:', jwtErr);
        }
      }
      
      let errorMsg = 'Failed to update maintenance mode. Please try again.';
      if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.response?.status === 403) {
        errorMsg = 'Authentication failed. Please login again.';
      }
      
      setErrorMessage(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      <div className="flex">
        <AdminSidebar />
        <main className="flex-1 p-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center mb-6">
              <div className="p-3 rounded-lg bg-yellow-50 text-yellow-600 mr-4">
                <Settings className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Maintenance Mode</h1>
                <p className="text-sm text-gray-500">Control site availability during maintenance periods</p>
                {lastUpdated && (
                  <p className="text-xs text-gray-400 mt-1">
                    Last updated: {new Date(lastUpdated).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="mb-8">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Maintenance Status</h3>
                    <p className="text-sm text-gray-500">
                      {isMaintenanceActive 
                        ? 'The site is currently in maintenance mode and unavailable to users.' 
                        : 'The site is currently live and available to all users.'}
                    </p>
                  </div>
                </div>
              </div>

              {successMessage && (
                <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200 flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-green-800">Success</h4>
                    <p className="text-sm text-green-600 mt-1">{successMessage}</p>
                  </div>
                </div>
              )}

              {errorMessage && (
                <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 flex items-start">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800">Error</h4>
                    <p className="text-sm text-red-600 mt-1">{errorMessage}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleToggleMaintenance} className="space-y-6">
                <div>
                  <label htmlFor="maintenanceMessage" className="block text-sm font-medium text-gray-700 mb-1">
                    Maintenance Message
                  </label>
                  <textarea
                    id="maintenanceMessage"
                    name="maintenanceMessage"
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    value={maintenanceMessage}
                    onChange={(e) => setMaintenanceMessage(e.target.value)}
                    placeholder="Explain why the site is down and when it will be back..."
                    required
                  />
                </div>

                <div>
                  <label htmlFor="estimatedTime" className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Downtime
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Clock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="estimatedTime"
                      name="estimatedTime"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      value={estimatedTime}
                      onChange={(e) => setEstimatedTime(e.target.value)}
                      placeholder="e.g. 2 hours, 30 minutes"
                      required
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className={`w-full flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${
                      isMaintenanceActive ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      isMaintenanceActive ? 'focus:ring-green-500' : 'focus:ring-yellow-500'
                    } transition-colors ${isSaving ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {isSaving ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {isMaintenanceActive ? 'Bringing Site Back Online...' : 'Enabling Maintenance Mode...'}
                      </>
                    ) : (
                      <>
                        {isMaintenanceActive ? (
                          <CheckCircle className="-ml-1 mr-2 h-5 w-5" />
                        ) : (
                          <AlertTriangle className="-ml-1 mr-2 h-5 w-5" />
                        )}
                        {isMaintenanceActive ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* Preview of maintenance mode */}
              {isMaintenanceActive && (
                <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Maintenance Mode Preview</h3>
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <div className="text-center py-8">
                      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-4">
                        <Settings className="h-8 w-8 text-yellow-600" />
                      </div>
                      <h2 className="text-xl font-semibold text-gray-800 mb-2">Under Maintenance</h2>
                      <p className="text-gray-600 mb-4">{maintenanceMessage}</p>
                      <p className="text-sm text-gray-500">
                        Estimated downtime: {estimatedTime}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MaintenanceMode;